import { Suspense, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowRight2, Category, Check, DirectInbox, Global, Logout, MagicStar, Microphone2, Moneys, Moon, Notification, Personalcard,
  SearchNormal1, Setting2, Shield, SidebarLeft, SidebarRight, Sun1, Ticket, User, Wifi,
} from '@/lib/iconsax';
import './shell.css';
import { useAuth, useWorkspace, WorkspaceProvider, type Membership } from '@/lib/auth';
import { LOCALES, useI18n } from '@/lib/i18n';
import { useTheme } from '@/lib/theme';
import { api } from '@/lib/api';
import { onRealtimeStatus, useRealtime, type RtStatus } from '@/lib/realtime';
import { useApi, useDebounced, useLocalStorage } from '@/lib/hooks';
import { initials, timeAgo } from '@/lib/format';
import { ADMIN_NAV, APP_NAV, DESK_NAV, titleFor, type NavGroup, type NavItem } from './nav';
import { LoadingRows, Menu, MenuItem, TopbarTitleContext, cx, useToast } from '@/components/ui';
import { platformCan } from '@/components/admin/perm';

/* ───────────────────────── Guards ───────────────────────── */
export function FullPageLoader() {
  return <div style={{ display: 'grid', placeItems: 'center', height: 'var(--app-h)' }} aria-busy="true"><BrandMark size={56} pulse alt="Loading Botifire" /></div>;
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { me, loading, mfaPending } = useAuth();
  const loc = useLocation();
  if (loading) return <FullPageLoader />;
  if (mfaPending) return <Navigate to="/mfa" replace state={{ from: loc.pathname }} />;
  if (!me) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  return <>{children}</>;
}

export function WorkspaceRoute() {
  const { slug } = useParams();
  const { me } = useAuth();
  const ws = me?.workspaces.find((w) => w.slug === slug);
  if (!me) return null;
  if (!ws) return <Navigate to="/" replace />;
  return <WorkspaceProvider ws={ws}><Outlet /></WorkspaceProvider>;
}

export function HomeRedirect() {
  const { me } = useAuth();
  if (!me) return <Navigate to="/login" replace />;
  let last: string | null = null;
  try { last = localStorage.getItem('bf_last_ws'); } catch { /* ignore */ }
  const ws = me.workspaces.find((w) => w.slug === last) ?? me.workspaces[0];
  if (!ws) return <Navigate to={me.user.platform_role !== 'none' ? '/admin' : '/new-workspace'} replace />;
  return <Navigate to={ws.role === 'agent' ? `/w/${ws.slug}/desk` : `/w/${ws.slug}`} replace />;
}

export function RequirePlatform({ children }: { children: ReactNode }) {
  const { me } = useAuth();
  if (!me) return null;
  if (me.user.platform_role === 'none' || me.impersonating) return <Navigate to="/" replace />;
  return <>{children}</>;
}

const allowed = (item: NavItem, can: (p: string) => boolean) =>
  !item.perm || (Array.isArray(item.perm) ? item.perm.some(can) : can(item.perm));

/* ───────────────────────── Brand ───────────────────────── */
/**
 * Botifire logo. `mark` is the candle "b"; `wordmark` is "botifire" with flame i-dots.
 * Both theme versions are rendered and CSS shows the one matching [data-theme].
 * `size` is the rendered height in px; width follows the artwork.
 */
export function BrandMark({ size = 34, kind = 'mark', pulse, alt = '' }: { size?: number; kind?: 'mark' | 'wordmark'; pulse?: boolean; alt?: string }) {
  return (
    <span className={cx('brand-logo', `brand-${kind}`, pulse && 'brand-pulse')} style={{ height: size }} role={alt ? 'img' : undefined} aria-label={alt || undefined} aria-hidden={alt ? undefined : true}>
      <img className="brand-on-light" src={`/brand/${kind}-light.png`} alt="" height={size} draggable={false} />
      <img className="brand-on-dark" src={`/brand/${kind}-dark.png`} alt="" height={size} draggable={false} />
    </span>
  );
}

/* ───────────────────────── Sidebar ───────────────────────── */
function NavList({ groups, base, can, onNavigate, counts }: { groups: NavGroup[]; base: string; can: (p: string) => boolean; onNavigate: () => void; counts?: Record<string, number> }) {
  const { t } = useI18n();
  return (
    <nav className="sb-nav" aria-label="Main">
      {groups.map((g, i) => {
        const items = g.items.filter((it) => allowed(it, can));
        if (!items.length) return null;
        return (
          <div className="sb-group" key={i}>
            {g.title && <div className="sb-group-title">{t(g.key ?? '', g.title)}</div>}
            {items.map((it) => (
              <NavLink key={it.to} to={base + it.to} end={it.end} onClick={onNavigate} title={t(it.key, it.label)}
                className={({ isActive }) => cx('sb-link', isActive && 'active')}>
                <it.icon size={22} color="currentColor" variant="Linear" aria-hidden />
                <span className="sb-label" data-accent-text>{t(it.key, it.label)}</span>
                {!!counts?.[it.key] && <span className="sb-count">{counts[it.key]}</span>}
              </NavLink>
            ))}
          </div>
        );
      })}
    </nav>
  );
}

function Sidebar({ home, sub, children, footer, collapsed, onToggle, mobileOpen }: {
  home: string; sub?: string; children: ReactNode; footer?: ReactNode; collapsed: boolean; onToggle: () => void; mobileOpen: boolean;
}) {
  return (
    <aside className={cx('sb', collapsed && 'collapsed', mobileOpen && 'open')}>
      <div className="sb-head">
        <Link to={home} className="sb-brand" aria-label="Botifire home">
          <span className="sb-brand-icon"><BrandMark size={40} /></span>
          <span className="sb-brand-text"><BrandMark kind="wordmark" size={36} />{sub && <span className="sb-brand-sub">{sub}</span>}</span>
        </Link>
        <button className="sb-toggle" onClick={onToggle} aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
          {collapsed ? <SidebarRight size={22} color="currentColor" /> : <SidebarLeft size={22} color="currentColor" />}
        </button>
      </div>
      {children}
      {footer && <div className="sb-foot">{footer}</div>}
    </aside>
  );
}

function SidebarFooter({ settingsTo, extra, card }: { settingsTo?: string; extra?: ReactNode; card?: ReactNode }) {
  const { logout } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  return (
    <>
      {card}
      {extra}
      {settingsTo && (
        <NavLink to={settingsTo} className={({ isActive }) => cx('sb-link', isActive && 'active')} title={t('nav.settings', 'Settings')}>
          <Setting2 size={22} color="currentColor" aria-hidden /><span className="sb-label" data-accent-text>{t('nav.settings', 'Settings')}</span>
        </NavLink>
      )}
      <button className="sb-link" onClick={async () => { await logout(); nav('/login'); }} title={t('common.signout', 'Log out')}>
        <Logout size={22} color="currentColor" aria-hidden /><span className="sb-label">{t('common.logout', 'Log out')}</span>
      </button>
    </>
  );
}

/** Gradient card at the bottom of the sidebar: pending invitations, or an invite prompt. Real data only. */
function TeamCard() {
  const { can, path } = useWorkspace();
  const [hidden, setHidden] = useLocalStorage<boolean>('bf_teamcard_hidden', false);
  const members = useApi<any>(can('members.read') ? '/api/v1/team/members' : null);
  const invites = useApi<any>(can('members.manage') ? '/api/v1/team/invitations' : null);
  const nav = useNavigate();
  if (hidden || !can('members.read') || !members.data) return null;
  const people: any[] = members.data.items ?? [];
  const pending = (invites.data?.items ?? []).filter((i: any) => !i.accepted_at && !i.revoked_at);
  const shown = people.slice(0, 4);
  const extra = people.length - shown.length;
  const text = pending.length
    ? `${pending.length} invitation${pending.length === 1 ? ' is' : 's are'} waiting to be accepted.`
    : people.length > 1 ? 'Your team answers here together. Invite more people to share the inbox.' : 'Invite your team to answer conversations with you.';
  return (
    <div className="team-card">
      <div className="team-avatars" aria-label={`${people.length} team member${people.length === 1 ? '' : 's'}`}>
        {shown.map((m) => <span key={m.id} className="team-avatar" title={m.name}>{m.avatar_url ? <img src={m.avatar_url} alt="" /> : initials(m.name)}</span>)}
        {extra > 0 && <span className="team-avatar more">{extra}+</span>}
      </div>
      <p>{text}</p>
      <div className="team-actions">
        <button className="btn btn-sm btn-outline-light" onClick={() => setHidden(true)}>Not now</button>
        {can('members.manage') && <button className="btn btn-sm btn-light" onClick={() => nav(path('/team'))}>{pending.length ? 'Review' : 'Invite'}</button>}
      </div>
    </div>
  );
}

/* ───────────────────────── Top bar ───────────────────────── */
const todayLabel = (locale: string) => new Date().toLocaleDateString(locale === 'en' ? 'en-GB' : locale, { day: '2-digit', month: 'long', year: 'numeric' })
  .replace(/^(\d{2}) (\S+) (\d{4})$/, '$1 $2, $3');

type Hit = { kind: string; label: string; sub?: string; to: string };

/** Search across conversations, contacts, tickets and agents the user can access. Voice input when the browser supports it. */
function GlobalSearch() {
  const { can, path } = useWorkspace();
  const nav = useNavigate();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [hits, setHits] = useState<Hit[]>([]);
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState(0);
  const [listening, setListening] = useState(false);
  const dq = useDebounced(q.trim(), 250);
  const box = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const Speech: any = (window as any).SpeechRecognition ?? (window as any).webkitSpeechRecognition;

  useEffect(() => {
    const f = (e: KeyboardEvent) => {
      if (e.key === '/' && !(e.target as HTMLElement)?.closest('input, textarea, [contenteditable]')) { e.preventDefault(); input.current?.focus(); }
    };
    document.addEventListener('keydown', f);
    return () => document.removeEventListener('keydown', f);
  }, []);
  useEffect(() => {
    const f = (e: MouseEvent) => { if (!box.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', f);
    return () => document.removeEventListener('mousedown', f);
  }, []);
  useEffect(() => {
    if (dq.length < 2) { setHits([]); return; }
    let live = true;
    setBusy(true);
    const reqs: Promise<Hit[]>[] = [];
    if (can('inbox.read_all') || can('inbox.read_assigned')) reqs.push(api('/api/v1/conversations', { query: { q: dq, limit: 4 } }).then((r: any) => r.items.map((c: any) => ({
      kind: 'Conversation', label: c.contact?.name || c.contact?.email || 'Visitor', sub: c.last_message_preview, to: path(`/inbox/${c.id}`) }))));
    if (can('contacts.read')) reqs.push(api('/api/v1/contacts', { query: { q: dq, limit: 4 } }).then((r: any) => r.items.map((c: any) => ({
      kind: 'Contact', label: c.name || c.email || c.phone || 'Unnamed contact', sub: c.email || c.phone, to: path(`/contacts/${c.id}`) }))));
    if (can('tickets.read')) reqs.push(api('/api/v1/tickets', { query: { q: dq, limit: 4 } }).then((r: any) => r.items.map((t: any) => ({
      kind: 'Ticket', label: `#${t.number} ${t.subject}`, sub: t.status, to: path(`/tickets/${t.id}`) }))));
    if (can('agents.read')) reqs.push(api('/api/v1/agents', { query: { q: dq, limit: 3 } }).then((r: any) => r.items.map((a: any) => ({
      kind: 'AI agent', label: a.name, sub: a.status, to: path(`/agents/${a.id}`) }))));
    Promise.allSettled(reqs).then((rs) => {
      if (!live) return;
      setHits(rs.flatMap((r) => (r.status === 'fulfilled' ? r.value : [])));
      setActive(0); setBusy(false);
    });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dq]);

  const go = (h?: Hit) => { if (!h) return; setOpen(false); setQ(''); nav(h.to); };
  const listen = () => {
    if (!Speech) return;
    const rec = new Speech();
    rec.lang = document.documentElement.lang === 'ur' ? 'ur-PK' : document.documentElement.lang === 'ar' ? 'ar' : 'en-US';
    rec.interimResults = true;
    rec.onresult = (e: any) => { const text = Array.from(e.results).map((r: any) => r[0].transcript).join(''); setQ(text); setOpen(true); };
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    setListening(true); rec.start();
  };
  const icons: Record<string, ReactNode> = {
    Conversation: <DirectInbox size={18} color="currentColor" />, Contact: <Personalcard size={18} color="currentColor" />,
    Ticket: <Ticket size={18} color="currentColor" />, 'AI agent': <MagicStar size={18} color="currentColor" />,
  };
  return (
    <div className="tb-search-wrap" ref={box}>
      <div className="tb-search">
        <span className="tb-search-icon"><SearchNormal1 size={20} color="currentColor" aria-hidden /></span>
        <input ref={input} value={q} placeholder="Search…" aria-label="Search conversations, contacts, tickets and agents"
          role="combobox" aria-autocomplete="list" aria-expanded={open && dq.length >= 2}
          aria-controls={open && dq.length >= 2 ? 'global-search-results' : undefined}
          aria-activedescendant={open && hits[active] ? `gs-${active}` : undefined}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, hits.length - 1)); }
            if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
            if (e.key === 'Enter') go(hits[active]);
            if (e.key === 'Escape') setOpen(false);
          }} />
      </div>
      {Speech && (
        <button className={cx('tb-round', listening && 'listening')} onClick={listen} aria-label={listening ? 'Listening…' : 'Search by voice'} title="Search by voice">
          <Microphone2 size={22} color="currentColor" />
        </button>
      )}
      {open && dq.length >= 2 && (
        <div className="tb-results" id="global-search-results" role="listbox">
          {busy && !hits.length && <div className="tb-results-empty">Searching…</div>}
          {!busy && !hits.length && <div className="tb-results-empty">No matches for “{dq}”.</div>}
          {hits.map((h, i) => (
            <button key={`${h.kind}-${h.to}`} id={`gs-${i}`} role="option" aria-selected={i === active} className={cx('tb-hit', i === active && 'active')}
              onMouseEnter={() => setActive(i)} onClick={() => go(h)}>
              <span className="tb-hit-icon">{icons[h.kind]}</span>
              <span className="stack" style={{ gap: 0, minWidth: 0, flex: 1 }}><span className="truncate">{h.label}</span>{h.sub && <span className="xs muted truncate">{h.sub}</span>}</span>
              <span className="xs faint">{h.kind}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ThemeToggle() {
  const { resolved, setPref } = useTheme();
  const set = (v: 'light' | 'dark') => { setPref(v); api('/api/v1/auth/me', { method: 'PATCH', body: { theme: v }, workspace: null }).catch(() => {}); };
  return (
    <div className="tb-theme" role="group" aria-label="Theme">
      <button aria-pressed={resolved === 'light'} onClick={() => set('light')} aria-label="Light theme"><Sun1 size={22} color="currentColor" /></button>
      <button aria-pressed={resolved === 'dark'} onClick={() => set('dark')} aria-label="Dark theme"><Moon size={22} color="currentColor" /></button>
    </div>
  );
}

function Notifications({ base }: { base: string }) {
  const { data, reload } = useApi<any[]>('/api/v1/notifications');
  const toast = useToast();
  const nav = useNavigate();
  useRealtime((e, d) => {
    if (e === 'conversation.assigned' || e === 'notification') reload();
    if (e === 'usage.threshold') toast(`You've used ${d.pct}% of your ${String(d.meter).replace('_', ' ')} allowance.`, 'info');
  });
  const unread = (data ?? []).filter((n) => !n.read_at).length;
  return (
    <Menu width={340} trigger={(p) => (
      <button className="tb-round" {...p} aria-label={`Notifications${unread ? `, ${unread} unread` : ''}`}>
        <Notification size={22} color="currentColor" />{unread > 0 && <span className="tb-dot" />}
      </button>
    )}>
      <div className="row" style={{ padding: '6px 10px' }}><strong>Notifications</strong><span className="spacer" />
        {unread > 0 && <button className="btn btn-ghost btn-sm" onClick={async () => { await api('/api/v1/notifications/read-all', { method: 'POST', body: {} }); reload(); }}>Mark all read</button>}
      </div>
      <div style={{ maxHeight: 360, overflowY: 'auto' }}>
        {(data ?? []).length === 0 && <div className="small muted" style={{ padding: '12px 10px' }}>You're all caught up.</div>}
        {(data ?? []).slice(0, 20).map((n) => (
          <button key={n.id} className="menu-item" style={{ alignItems: 'flex-start' }} onClick={() => n.link && nav(base + n.link)}>
            <span style={{ width: 8, height: 8, borderRadius: 4, marginTop: 6, background: n.read_at ? 'transparent' : 'var(--orange)', flex: 'none' }} />
            <span className="stack" style={{ gap: 2 }}><span style={{ fontWeight: 500 }}>{n.title}</span>{n.body && <span className="xs muted">{n.body}</span>}<span className="xs faint">{timeAgo(n.created_at)}</span></span>
          </button>
        ))}
      </div>
    </Menu>
  );
}

const ROLE_LABEL: Record<string, string> = {
  owner: 'Owner', admin: 'Admin', manager: 'Team manager', bot_designer: 'Bot designer', agent: 'Support agent',
  marketing: 'Marketing', analyst: 'Analyst', support: 'Platform support',
};
const PRESENCE = { online: 'var(--green-vivid)', away: 'var(--orange)', offline: 'var(--grey-300)' } as Record<string, string>;

/** Profile chip (avatar, name, role, chevron) with account, availability, language and sign-out. */
export function UserMenu({ roleLabel, presence = false }: { roleLabel?: string; presence?: boolean }) {
  const { me, logout, refresh } = useAuth();
  const { locale, setLocale, t } = useI18n();
  const nav = useNavigate();
  const ws = useOptionalWorkspace();
  const [avail, setAvail] = useState(ws?.ws.availability ?? 'offline');
  // Keep the menu in step with the saved status (workspace switch, refresh after saving, other tabs).
  useEffect(() => { if (ws) setAvail(ws.ws.availability ?? 'offline'); }, [ws?.ws.id, ws?.ws.availability]); // eslint-disable-line react-hooks/exhaustive-deps
  const trial = useTrialLabel();
  if (!me) return null;
  const setAvailability = async (v: string) => {
    const before = avail;
    setAvail(v);
    try { await api('/api/v1/workspace/availability', { method: 'PUT', body: { availability: v } }); }
    catch { setAvail(before); return; }
    refresh();
  };
  const baseRole = roleLabel ?? (ws ? ROLE_LABEL[ws.ws.role] ?? ws.ws.role : ROLE_LABEL[me.user.platform_role] ?? '');
  const role = trial ? `${baseRole} · ${trial}` : baseRole;
  return (
    <Menu width={250} trigger={(p) => (
      <button className="tb-profile" {...p} aria-label={`Account menu for ${me.user.name}`}>
        <span className="tb-avatar">
          {me.user.avatar_url ? <img src={me.user.avatar_url} alt="" /> : initials(me.user.name)}
          {presence && <span className="tb-presence" style={{ background: PRESENCE[avail] }} />}
        </span>
        <span className="tb-profile-text"><span className="tb-profile-name">{me.user.name}</span><span className="tb-profile-role">{role}</span></span>
        <ArrowRight2 size={20} color="currentColor" className="tb-profile-chevron" />
      </button>
    )}>
      <div style={{ padding: '8px 10px' }}><div style={{ fontWeight: 600 }}>{me.user.name}</div><div className="xs muted truncate">{me.user.email}</div></div>
      <div className="menu-sep" />
      {presence && (
        <>
          <div className="menu-label">Availability</div>
          {(['online', 'away', 'offline'] as const).map((v) => (
            <MenuItem key={v} icon={avail === v ? <Check /> : <span className="pdot" style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 5, margin: 3, background: PRESENCE[v] }} />} onClick={() => setAvailability(v)}>
              {t(`presence.${v}`, v[0]!.toUpperCase() + v.slice(1))}
            </MenuItem>
          ))}
          <div className="menu-sep" />
        </>
      )}
      {trial && ws?.can('billing.manage') && <MenuItem icon={<Moneys size={18} color="currentColor" />} onClick={() => nav(ws.path('/billing'))}>Choose a plan</MenuItem>}
      <MenuItem icon={<User size={18} color="currentColor" />} onClick={() => nav('/account')}>Account & security</MenuItem>
      {me.user.platform_role !== 'none' && !me.impersonating && <MenuItem icon={<Shield size={18} color="currentColor" />} onClick={() => nav('/admin')}>{t('nav.admin', 'Platform admin')}</MenuItem>}
      <div className="menu-sep" />
      <div className="menu-label">Language</div>
      {LOCALES.map((l) => (
        <MenuItem key={l.code} icon={locale === l.code ? <Check /> : <Global size={18} color="currentColor" />} onClick={() => { setLocale(l.code); api('/api/v1/auth/me', { method: 'PATCH', body: { locale: l.code }, workspace: null }).catch(() => {}); }}>{l.label}</MenuItem>
      ))}
      <div className="menu-sep" />
      <MenuItem icon={<Logout size={18} color="currentColor" />} onClick={async () => { await logout(); nav('/login'); }}>{t('common.signout', 'Sign out')}</MenuItem>
    </Menu>
  );
}

function useOptionalWorkspace() {
  try { return useWorkspace(); } catch { return null; }
}

function RealtimeIndicator() {
  const [s, setS] = useState<RtStatus>('connecting');
  // Live updates only exist inside a workspace; platform admin and account pages have no socket to (re)connect.
  const inWorkspace = !!useOptionalWorkspace();
  useEffect(() => onRealtimeStatus(setS), []);
  if (s === 'open' || !inWorkspace) return null;
  return <span className="rt-status" role="status"><Wifi size={14} color="currentColor" />{s === 'connecting' ? 'Connecting…' : 'Reconnecting…'}</span>;
}

function Topbar({ title, onMenu, search, presence, roleLabel, notificationsBase, extra }: {
  title: string; onMenu: () => void; search?: boolean; presence?: boolean; roleLabel?: string; notificationsBase?: string; extra?: ReactNode;
}) {
  const { locale } = useI18n();
  return (
    <header className="tb">
      <button className="tb-round tb-menu" onClick={onMenu} aria-label="Open navigation"><Category size={22} color="currentColor" /></button>
      <div className="tb-title"><h1>{title}</h1><span className="tb-date">{todayLabel(locale)}</span></div>
      {search ? <GlobalSearch /> : <span className="spacer" />}
      <span className="tb-gap" />
      <RealtimeIndicator />
      {extra}
      <ThemeToggle />
      {notificationsBase !== undefined && <Notifications base={notificationsBase} />}
      <UserMenu presence={presence} roleLabel={roleLabel} />
    </header>
  );
}

/** Trial status shown under the user's name in the profile chip (links to billing from the menu). */
function useTrialLabel(): string | null {
  const ws = useOptionalWorkspace();
  const { data } = useApi<any>(ws && ws.can('billing.read') ? '/api/v1/workspace' : null);
  if (!data || data.plan?.status !== 'trialing' || !data.plan?.trial_ends_at) return null;
  const days = Math.max(0, Math.ceil((new Date(data.plan.trial_ends_at).getTime() - Date.now()) / 86400000));
  return `Trial · ${days} day${days === 1 ? '' : 's'} left`;
}

function ImpersonationBar() {
  const { me, refresh } = useAuth();
  const nav = useNavigate();
  if (!me?.impersonating) return null;
  const end = async () => {
    const r = await api('/api/v1/support-access/end', { method: 'POST', body: {}, workspace: null }).catch(() => null);
    const again = await refresh();
    nav(r?.restored_staff_session && again ? '/admin/workspaces' : '/login', { replace: true });
  };
  return (
    <div className="impersonation-bar" role="status">
      <span>Support access session — every action is recorded in this workspace's audit log.</span>
      <button onClick={end}>End support session</button>
    </div>
  );
}

function useShellState(key: string) {
  const [collapsed, setCollapsed] = useLocalStorage<boolean>(`bf_sb_${key}`, false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => setMobileOpen(false), [loc.pathname]);
  return { collapsed, toggle: () => setCollapsed(!collapsed), mobileOpen, setMobileOpen };
}

/* ───────────────────────── Customer panel ───────────────────────── */
export function AppShell() {
  const { ws, can, base, path } = useWorkspace();
  const s = useShellState('app');
  const loc = useLocation();
  const full = /\/(inbox|flows\/[^/]+|workflows\/[^/]+)(\/|$)/.test(loc.pathname);
  const title = useMemo(() => {
    if (loc.pathname.startsWith(`${base}/settings`)) return 'Settings';
    return titleFor(loc.pathname, base, APP_NAV) ?? ws.name;
  }, [loc.pathname, base, ws.name]);
  return (
    <div className={cx('shell', s.collapsed && 'sb-collapsed', s.mobileOpen && 'nav-open')}>
      <Sidebar home={base} collapsed={s.collapsed} onToggle={s.toggle} mobileOpen={s.mobileOpen}
        footer={<SidebarFooter settingsTo={can('workspace.read') ? path('/settings') : undefined} card={s.collapsed ? undefined : <TeamCard />}
          extra={can('inbox.reply') ? <NavLink to={`${base}/desk`} className="sb-link" title="Agent desk"><DirectInbox size={22} color="currentColor" aria-hidden /><span className="sb-label">Agent desk</span></NavLink> : undefined} />}>
        <div className="sb-scroll">
          <WorkspaceSwitcher />
          <NavList groups={APP_NAV} base={base} can={can} onNavigate={() => s.setMobileOpen(false)} />
        </div>
      </Sidebar>
      <div className="shell-main">
        <ImpersonationBar />
        <Topbar title={title} onMenu={() => s.setMobileOpen(true)} search presence={can('inbox.reply')} notificationsBase={base} />
        <main className={cx('shell-content', full && 'full')} id="main">
          <TopbarTitleContext.Provider value={title}><Suspense fallback={<div className="page"><LoadingRows /></div>}><Outlet /></Suspense></TopbarTitleContext.Provider>
        </main>
      </div>
      {s.mobileOpen && <div className="sb-scrim" onClick={() => s.setMobileOpen(false)} aria-hidden />}
    </div>
  );
}

function WorkspaceSwitcher() {
  const { me } = useAuth();
  const { ws } = useWorkspace();
  const nav = useNavigate();
  if ((me?.workspaces.length ?? 0) < 2) return null;
  return (
    <Menu align="start" width={244} trigger={(p) => (
      <button className="sb-ws" {...p} title="Switch workspace">
        <span className="sb-ws-initial">{ws.name.slice(0, 1).toUpperCase()}</span>
        <span className="sb-label truncate">{ws.name}</span>
      </button>
    )}>
      <div className="menu-label">Workspaces</div>
      {me?.workspaces.map((w: Membership) => (
        <MenuItem key={w.id} icon={w.id === ws.id ? <Check /> : <span style={{ width: 16 }} />} onClick={() => nav(`/w/${w.slug}`)}>{w.name}</MenuItem>
      ))}
      <div className="menu-sep" />
      <MenuItem onClick={() => nav('/new-workspace')}>New workspace</MenuItem>
    </Menu>
  );
}

/* ───────────────────────── Agent desk ───────────────────────── */
export function DeskShell() {
  const { ws, can, base } = useWorkspace();
  const s = useShellState('desk');
  const loc = useLocation();
  const { data: counts, reload } = useApi<Record<string, number>>('/api/v1/desk/counts');
  useRealtime((e) => { if (e.startsWith('conversation.') || e === 'message.received') reload(); });
  const navCounts = { 'desk.mine': counts?.mine ?? 0, 'desk.queue': counts?.unassigned ?? 0 };
  const deskBase = `${base}/desk`;
  const title = titleFor(loc.pathname.replace(/\/(c|queue)\/[^/]+$/, (m) => (m.startsWith('/queue') ? '/queue' : '')), deskBase, DESK_NAV) ?? 'Agent desk';
  return (
    <div className={cx('shell', s.collapsed && 'sb-collapsed', s.mobileOpen && 'nav-open')}>
      <Sidebar home={deskBase} sub="Agent desk" collapsed={s.collapsed} onToggle={s.toggle} mobileOpen={s.mobileOpen}
        footer={<SidebarFooter extra={ws.role !== 'agent' ? <NavLink to={base} className="sb-link" title="Workspace admin"><Category size={22} color="currentColor" aria-hidden /><span className="sb-label">Workspace admin</span></NavLink> : undefined} />}>
        <div className="sb-scroll">
          <NavList groups={[{ items: DESK_NAV }]} base={deskBase} can={can} onNavigate={() => s.setMobileOpen(false)} counts={navCounts} />
        </div>
      </Sidebar>
      <div className="shell-main">
        <ImpersonationBar />
        <Topbar title={title} onMenu={() => s.setMobileOpen(true)} search presence notificationsBase={base} />
        <main className="shell-content full" id="main"><TopbarTitleContext.Provider value={title}><Suspense fallback={<LoadingRows />}><Outlet /></Suspense></TopbarTitleContext.Provider></main>
      </div>
      {s.mobileOpen && <div className="sb-scrim" onClick={() => s.setMobileOpen(false)} aria-hidden />}
    </div>
  );
}

/* ───────────────────────── Platform admin ───────────────────────── */
export function AdminShell() {
  const { me } = useAuth();
  const s = useShellState('admin');
  const loc = useLocation();
  const title = titleFor(loc.pathname, '/admin', ADMIN_NAV) ?? 'Platform administration';
  return (
    <div className={cx('shell', s.collapsed && 'sb-collapsed', s.mobileOpen && 'nav-open')}>
      <Sidebar home="/admin" sub="Platform admin" collapsed={s.collapsed} onToggle={s.toggle} mobileOpen={s.mobileOpen}
        footer={<SidebarFooter extra={<NavLink to="/" end className="sb-link" title="My workspaces"><Category size={22} color="currentColor" aria-hidden /><span className="sb-label">My workspaces</span></NavLink>} />}>
        <div className="sb-scroll">
          <NavList groups={ADMIN_NAV} base="/admin" can={(p) => platformCan(me?.user.platform_role, p)} onNavigate={() => s.setMobileOpen(false)} />
        </div>
      </Sidebar>
      <div className="shell-main">
        <Topbar title={title} onMenu={() => s.setMobileOpen(true)} />
        <main className="shell-content" id="main"><TopbarTitleContext.Provider value={title}><Suspense fallback={<div className="page"><LoadingRows /></div>}><Outlet /></Suspense></TopbarTitleContext.Provider></main>
      </div>
      {s.mobileOpen && <div className="sb-scrim" onClick={() => s.setMobileOpen(false)} aria-hidden />}
    </div>
  );
}
